"use client";

import * as React from "react";
import { Dialog as D, Popover as P, Tooltip as T } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

/* ---------- Tooltip ---------- */

export const TooltipProvider = T.Provider;

export function Tip({
  content,
  children,
  side = "bottom",
  shortcut,
}: {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  shortcut?: string;
}) {
  if (!content) return <>{children}</>;
  return (
    <T.Root>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className="z-[60] max-w-80 rounded-sm border border-border bg-surface px-2 py-1 text-xs text-fg shadow-popover animate-in"
        >
          {content}
          {shortcut ? <span className="ml-2 text-subtle tnum">{shortcut}</span> : null}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

/* ---------- Popover ---------- */

export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;
export const PopoverClose = P.Close;

export function PopoverContent({ className, align = "start", sideOffset = 4, ...props }: React.ComponentProps<typeof P.Content>) {
  return (
    <P.Portal>
      <P.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn("z-50 rounded-lg border border-border bg-surface text-sm text-fg shadow-popover outline-none animate-in", className)}
        {...props}
      />
    </P.Portal>
  );
}

/* ---------- Dialog ---------- */

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export function DialogContent({
  title,
  children,
  className,
  footer,
  width = 440,
  ...props
}: React.ComponentProps<typeof D.Content> & { title: React.ReactNode; footer?: React.ReactNode; width?: number }) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 bg-overlay animate-in" />
      <D.Content
        className={cn(
          "fixed top-[18%] left-1/2 z-50 flex max-h-[76vh] max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col rounded-lg border border-border bg-surface text-sm text-fg shadow-popover outline-none animate-in",
          className,
        )}
        style={{ width }}
        aria-describedby={undefined}
        {...props}
      >
        <div className="flex h-10 shrink-0 items-center justify-between border-b border-border pr-1.5 pl-4">
          <D.Title className="text-base font-semibold">{title}</D.Title>
          <D.Close asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Fechar">
              <X />
            </Button>
          </D.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
        {footer ? <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-surface-2 px-4 py-2.5 rounded-b-lg">{footer}</div> : null}
      </D.Content>
    </D.Portal>
  );
}
